import { useState, useEffect, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';

const DEFAULT_ORDER = ['discover', 'services', 'playlists', 'my_music', 'local_files', 'tools'];
const LS_KEY = 'maestro_sidebar_section_order';

function getToken() {
  try {
    const raw = localStorage.getItem('maestro_auth');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.accessToken || null;
    }
  } catch { /* ignore */ }
  return null;
}

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function writeLocal(order) {
  try {
    if (order) {
      localStorage.setItem(LS_KEY, JSON.stringify(order));
    } else {
      localStorage.removeItem(LS_KEY);
    }
  } catch { /* ignore */ }
}

function reconcile(saved, knownIds) {
  if (!saved || !Array.isArray(saved)) return null;
  const filtered = saved.filter(id => knownIds.includes(id));
  const missing = knownIds.filter(id => !filtered.includes(id));
  const result = [...filtered, ...missing];
  if (result.length === knownIds.length &&
      result.every((id, i) => id === DEFAULT_ORDER[i])) {
    return null;
  }
  return result;
}

export default function useSidebarOrder() {
  const [order, setOrder] = useState(() => readLocal());
  const patchTimer = useRef(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const token = getToken();
    if (!token) return;

    fetch(`${CLOUD_API_URL}/api/user/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(prefs => {
        if (!prefs) return;
        const remote = prefs.sidebar_section_order ?? null;
        const reconciled = reconcile(remote, DEFAULT_ORDER);
        setOrder(reconciled);
        writeLocal(reconciled);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      const token = getToken();
      if (!token) return;
      fetch(`${CLOUD_API_URL}/api/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(prefs => {
          if (!prefs) return;
          const remote = prefs.sidebar_section_order ?? null;
          const reconciled = reconcile(remote, DEFAULT_ORDER);
          setOrder(reconciled);
          writeLocal(reconciled);
        })
        .catch(() => {});
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const persistToServer = useCallback((newOrder) => {
    if (patchTimer.current) clearTimeout(patchTimer.current);
    patchTimer.current = setTimeout(() => {
      const token = getToken();
      if (!token) return;
      fetch(`${CLOUD_API_URL}/api/user/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sidebar_section_order: newOrder }),
      }).catch(() => {});
    }, 400);
  }, []);

  const updateOrder = useCallback((newOrder) => {
    const resolved = reconcile(newOrder, DEFAULT_ORDER);
    setOrder(resolved);
    writeLocal(resolved);
    persistToServer(resolved);
  }, [persistToServer]);

  const resetOrder = useCallback(() => {
    setOrder(null);
    writeLocal(null);
    persistToServer(null);
  }, [persistToServer]);

  const applySectionOrder = useCallback((sections) => {
    if (!order) return sections;
    const topNav = sections.find(s => s.id === 'top_nav');
    const sortable = sections.filter(s => s.id !== 'top_nav');
    const map = new Map(sortable.map(s => [s.id, s]));
    const ordered = [];
    for (const id of order) {
      const section = map.get(id);
      if (section) {
        ordered.push(section);
        map.delete(id);
      }
    }
    for (const remaining of map.values()) {
      ordered.push(remaining);
    }
    return topNav ? [topNav, ...ordered] : ordered;
  }, [order]);

  const sortableIds = order || DEFAULT_ORDER;

  return { order, sortableIds, updateOrder, resetOrder, applySectionOrder };
}
